/** Labels and tones for data-quality enums (English source strings; translate with t()). */
import { formatCalendarDate, formatDate } from '@/lib/format';
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';

type Label = { label: string; tone: Tone; description?: string };

export const SEVERITY: Record<string, Label> = {
  low: { label: msg('Low'), tone: 'info' },
  medium: { label: msg('Medium'), tone: 'medium' },
  high: { label: msg('High'), tone: 'bad' },
};

export const FINDING_STATUS: Record<string, Label> = {
  proposed: { label: msg('Proposed'), tone: 'accent', description: msg('Proposed by an AI audit; a person confirms or dismisses it.') },
  open: { label: msg('Open'), tone: 'medium' },
  acknowledged: { label: msg('Acknowledged'), tone: 'info', description: msg('Seen and being handled; it still affects trust.') },
  resolved: { label: msg('Resolved'), tone: 'good', description: msg('The check passes again.') },
  dismissed: { label: msg('Dismissed'), tone: 'neutral', description: msg('Not a real problem: the data was right.') },
};

export const SOURCE: Record<string, string> = {
  deterministic: msg('Check'),
  drift: msg('Drift'),
  ai: msg('AI audit'),
};

/** Capability trust states (ADR-0010 §2). */
export const TRUST: Record<string, Label> = {
  trusted: { label: msg('Trusted'), tone: 'good' },
  degraded: { label: msg('Degraded'), tone: 'medium', description: msg('Still used for scores, but flagged.') },
  unobserved: { label: msg('Unobserved'), tone: 'caution', description: msg('Went silent while driving: not observed, which is not the same as safe.') },
  untrusted: { label: msg('Untrusted'), tone: 'bad', description: msg('Being misread: scores that depend on it become "not observable" instead of wrong.') },
};

export const ISSUE: Record<string, Label> = {
  offline: { label: msg('Offline'), tone: 'bad', description: msg('No data for longer than the silence limit.') },
  low_voltage: { label: msg('Low voltage'), tone: 'medium', description: msg('Supply voltage at or below the minimum.') },
  poor_gps: { label: msg('Poor GPS'), tone: 'medium', description: msg('Less than 80 % of messages have a valid position.') },
  data_quality: { label: msg('Data findings'), tone: 'caution', description: msg('Open data-quality findings for this device.') },
  installation_failed: { label: msg('Installation failed'), tone: 'bad', description: msg('The latest installation validation failed.') },
};

export const VERDICT: Record<string, Label> = {
  pass: { label: msg('Pass'), tone: 'good' },
  warn: { label: msg('Warning'), tone: 'medium' },
  fail: { label: msg('Fail'), tone: 'bad' },
};

export const AUDIT_STATUS: Record<string, Label> = {
  queued: { label: msg('Queued'), tone: 'neutral' },
  running: { label: msg('Running'), tone: 'info' },
  done: { label: msg('Done'), tone: 'good' },
  skipped: { label: msg('Skipped'), tone: 'neutral' },
  failed: { label: msg('Failed'), tone: 'bad' },
};

export const AI_VERDICT: Record<string, Label> = {
  plausible: { label: msg('Plausible'), tone: 'good' },
  suspicious: { label: msg('Suspicious'), tone: 'medium' },
  broken: { label: msg('Broken'), tone: 'bad' },
};

export const TRIGGER: Record<string, string> = { schedule: msg('Scheduled'), manual: msg('On request'), auto: msg('Automatic') };

export function labelOf(map: Record<string, { label: string } | string>, key: string | null | undefined): string {
  if (!key) return '—';
  const v = map[key];
  if (!v) return key.replace(/[._]/g, ' ');
  return t(typeof v === 'string' ? v : v.label);
}

/** Check names by key (the server sends English labels; these translate). Unknown keys fall back. */
export const CHECK: Record<string, string> = {
  'signal.missing': msg('Expected signal missing'),
  'sampling.interval': msg('Slow sampling'),
  'data.gaps': msg('Data gaps'),
  'data.duplicates': msg('Duplicate messages'),
  'clock.future': msg('Clock ahead'),
  'clock.out_of_order': msg('Out-of-order messages'),
  'gps.jump': msg('GPS jumps'),
  'gps.zero_fix': msg('(0,0) positions'),
  'odometer.monotonic': msg('Odometer goes backwards'),
  'odometer.gps_mismatch': msg('Odometer vs GPS distance'),
  'fuel.monotonic': msg('Fuel counter goes backwards'),
  'fuel.implausible': msg('Implausible fuel use'),
  'can.speed_mismatch': msg('CAN speed vs GPS speed'),
  'rpm.implausible': msg('Implausible RPM'),
  'ignition.flapping': msg('Ignition flapping'),
  'signal.stuck': msg('Stuck value'),
  'accelerometer.orientation': msg('Accelerometer orientation'),
  'dsm.blocked': msg('Driver camera blocked'),
  'harsh.no_speed_change': msg('Harsh events without speed change'),
  'profile.unmapped': msg('Unmapped parameters'),
  'connection.silent': msg('No data from a fitted device'),
  'events.burst': msg('Event burst'),
  'events.sensitive': msg('Device harsh thresholds too sensitive'),
  'events.silent': msg('Events stopped'),
  'drift.peer': msg('Differs from peers'),
  'drift.baseline': msg('Differs from own history'),
  'drift.sampling': msg('Sampling changed'),
  'drift.vanished': msg('Parameter vanished'),
};

/** A check's name: exact key, else its parent (drift.peer.rpm → drift.peer), else the server's label. */
export function checkLabel(key: string, fallback?: string | null): string {
  const hit = CHECK[key] ?? CHECK[key.split('.').slice(0, -1).join('.')];
  return hit ? t(hit) : fallback || key;
}

/** When a finding last failed: its last local day (data time), never a relative time — the
 * evidence can be about a day that ends after "now". */
export function lastFailed(f: { last_day?: string | null; last_seen: string }): string {
  return f.last_day ? formatCalendarDate(f.last_day) : formatDate(f.last_seen);
}
